import { useState, useEffect } from 'react';
import api from '../lib/api.js';
import PageHeader from '../components/ui/PageHeader.jsx';
import WatermarkPosition from '../components/WatermarkPosition.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [securityMode, setSecurityMode] = useState(''); // '' | 'pin' | 'password' | 'email'
  const [secLoading, setSecLoading] = useState(false);
  const [secMsg, setSecMsg] = useState('');
  const [sec, setSec] = useState({ current_password: '', current_pin: '', current_email: '', new_password: '', new_pin: '', new_email: '' });

  useEffect(() => { loadSettings(); }, []);

  async function loadSettings() {
    const res = await api.get('/settings');
    if (res.ok) setSettings(res.data);
  }

  function set(path, value) {
    setSettings(prev => {
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
  }

  function handleWatermarkChange(changes) {
    // changes contiene: { type, position, rotation, size, _text }
    if (changes.type !== undefined) set('branding.watermark_type', changes.type);
    if (changes.position !== undefined) set('branding.watermark_position', changes.position);
    if (changes.rotation !== undefined) set('branding.watermark_rotation', changes.rotation);
    if (changes.size !== undefined) set('branding.watermark_size', changes.size);
    if (changes._text !== undefined) set('branding.watermark_text', changes._text);
  }

  /**
   * Il logo viene letto localmente e salvato come data URL nell'impostazione
   * branding.logo_data_url: nessuna upload route su disco, il DB resta l'unico
   * stato persistente (importante nel deploy container, dove il filesystem e'
   * effimero e solo il volume del database viene conservato).
   */
  const MAX_LOGO_BYTES = 512 * 1024;

  function handleLogoSelect(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage('Il file selezionato non è un’immagine');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setMessage('Logo troppo grande: massimo 512 KB');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      set('branding.logo_data_url', String(reader.result));
      setMessage('Logo caricato: premi “Salva impostazioni” per conservarlo');
    };
    reader.onerror = () => setMessage('Lettura del file non riuscita');
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/settings', settings);
      if (res.ok) setMessage('Impostazioni salvate con successo ✓');
      else setMessage('Errore salvataggio: ' + (res.error || 'sconosciuto'));
    } catch (err) {
      setMessage('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function changePin(e) {
    e.preventDefault();
    setSecMsg('');
    setSecLoading(true);
    try {
      await api.post('/auth/change-pin', { current_password: sec.current_password, current_pin: sec.current_pin, new_pin: sec.new_pin });
      setSecMsg('PIN modificato con successo');
      setSec({ ...sec, current_password: '', current_pin: '', new_pin: '' });
      refresh();
    } catch (err) {
      setSecMsg(err?.message || 'Cambio PIN fallito');
    } finally {
      setSecLoading(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setSecMsg('');
    setSecLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: sec.current_password, pin: sec.current_pin, new_password: sec.new_password });
      setSecMsg('Password modificata con successo');
      setSec({ ...sec, current_password: '', current_pin: '', new_password: '' });
      refresh();
    } catch (err) {
      setSecMsg(err?.message || 'Cambio password fallito');
    } finally {
      setSecLoading(false);
    }
  }

  async function changeEmail(e) {
    e.preventDefault();
    setSecMsg('');
    setSecLoading(true);
    try {
      await api.post('/auth/change-email', { current_password: sec.current_password, current_pin: sec.current_pin, new_email: sec.new_email });
      setSecMsg('Email modificata con successo');
      setSec({ ...sec, current_password: '', current_pin: '', new_email: '' });
      refresh();
    } catch (err) {
      setSecMsg(err?.message || 'Cambio email fallito');
    } finally {
      setSecLoading(false);
    }
  }

  if (!settings) return <div className="text-surface-4 py-8 text-center">Caricamento...</div>;

  const profile = settings.profile || {};
  const tax = settings.tax || {};
  const quote = settings.quote || {};
  const branding = settings.branding || {};
  const ui = settings.ui || {};

  return (
    <>
      <PageHeader
        title="Impostazioni"
        subtitle="Profilo, tassazione, preventivi, branding e UI"
      />

      {message && (
        <div className="p-3 rounded-md text-sm mb-4" style={{
          background: message.includes('✓') ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)',
          color: message.includes('✓') ? '#22c55e' : '#ef4444',
          border: '1px solid ' + (message.includes('✓') ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)')
        }}>{message}</div>
      )}

      {/* Sicurezza */}
      <div className="card-elevated p-6 mb-6" id="sicurezza">
        <h2 className="text-lg font-semibold text-surface mb-4">Sicurezza</h2>
        <div className="space-y-6">
          {/* Cambia password */}
          <form onSubmit={changePassword} className="space-y-3">
            <h3 className="text-sm font-medium text-surface-2">Cambia password</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Password attuale"><input className="input" type="password" autoComplete="current-password" value={sec.current_password} onChange={e => setSec({ ...sec, current_password: e.target.value })} /></Field>
              <Field label="PIN attuale"><input className="input" type="password" inputMode="numeric" maxLength={6} autoComplete="off" value={sec.current_pin} onChange={e => setSec({ ...sec, current_pin: e.target.value })} placeholder="******" /></Field>
            </div>
            <Field label="Nuova password (min 6 caratteri)"><input className="input" type="password" autoComplete="new-password" required minLength={6} value={sec.new_password} onChange={e => setSec({ ...sec, new_password: e.target.value })} /></Field>
            {secMsg && <p className={`text-xs ${secMsg.includes('modificata') ? 'text-success' : 'text-danger'}`}>{secMsg}</p>}
            <button type="submit" className="btn btn-primary text-xs" disabled={secLoading}>{secLoading ? 'Salvataggio...' : 'Salva password'}</button>
          </form>
          <hr style={{ borderColor: 'var(--color-line)' }} />
          {/* Cambia PIN */}
          <form onSubmit={changePin} className="space-y-3">
            <h3 className="text-sm font-medium text-surface-2">Cambia PIN</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Password attuale"><input className="input" type="password" autoComplete="current-password" value={sec.current_password} onChange={e => setSec({ ...sec, current_password: e.target.value })} /></Field>
              <Field label="PIN attuale"><input className="input" type="password" inputMode="numeric" maxLength={6} autoComplete="off" value={sec.current_pin} onChange={e => setSec({ ...sec, current_pin: e.target.value })} placeholder="******" /></Field>
            </div>
            <Field label="Nuovo PIN (4-6 cifre)"><input className="input" type="password" inputMode="numeric" pattern="[0-9]{4,6}" maxLength={6} required autoComplete="off" value={sec.new_pin} onChange={e => setSec({ ...sec, new_pin: e.target.value })} placeholder="******" /></Field>
            {secMsg && <p className={`text-xs ${secMsg.includes('modificata') ? 'text-success' : 'text-danger'}`}>{secMsg}</p>}
            <button type="submit" className="btn btn-primary text-xs" disabled={secLoading}>{secLoading ? 'Salvataggio...' : 'Salva PIN'}</button>
          </form>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profilo */}
        <div className="card-elevated p-6">
          <h2 className="text-lg font-semibold text-surface mb-4">Profilo Professionale</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome"><input className="input" value={profile.first_name || ''} onChange={e => set('profile.first_name', e.target.value)} /></Field>
              <Field label="Cognome"><input className="input" value={profile.last_name || ''} onChange={e => set('profile.last_name', e.target.value)} /></Field>
            </div>
            <Field label="Ragione sociale"><input className="input" value={profile.business_name || ''} onChange={e => set('profile.business_name', e.target.value)} /></Field>
            <Field label="Ruolo"><input className="input" value={profile.job_title || ''} onChange={e => set('profile.job_title', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="P.IVA"><input className="input" value={profile.vat_number || ''} onChange={e => set('profile.vat_number', e.target.value)} /></Field>
              <Field label="Codice Fiscale"><input className="input" value={profile.tax_code || ''} onChange={e => set('profile.tax_code', e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><input className="input" value={profile.email || ''} onChange={e => set('profile.email', e.target.value)} /></Field>
              <Field label="Telefono"><input className="input" value={profile.phone || ''} onChange={e => set('profile.phone', e.target.value)} /></Field>
            </div>
            <Field label="PEC"><input className="input" value={profile.pec || ''} onChange={e => set('profile.pec', e.target.value)} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Via"><input className="input" value={profile.street || ''} onChange={e => set('profile.street', e.target.value)} /></Field>
              <Field label="Città"><input className="input" value={profile.city || ''} onChange={e => set('profile.city', e.target.value)} /></Field>
              <Field label="CAP"><input className="input" value={profile.zip || ''} onChange={e => set('profile.zip', e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Provincia"><input className="input" value={profile.province || ''} onChange={e => set('profile.province', e.target.value)} /></Field>
              <Field label="Stato"><input className="input" value={profile.country || 'Italia'} onChange={e => set('profile.country', e.target.value)} /></Field>
            </div>
            <Field label="IBAN"><input className="input" value={profile.bank_iban || ''} onChange={e => set('profile.bank_iban', e.target.value)} /></Field>
            <Field label="Intestatario"><input className="input" value={profile.bank_holder || ''} onChange={e => set('profile.bank_holder', e.target.value)} /></Field>
          </div>
        </div>

        {/* Tassazione */}
        <div className="card-elevated p-6">
          <h2 className="text-lg font-semibold text-surface mb-4">Tassazione</h2>
          <div className="space-y-4">
            <Field label="Regime">
              <select className="input" value={tax.regime || 'forfettario'} onChange={e => set('tax.regime', e.target.value)}>
                <option value="forfettario">Forfettario</option>
                <option value="ordinary">Ordinario</option>
              </select>
            </Field>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tax.inps_enabled} onChange={e => set('tax.inps_enabled', e.target.checked)} />
                <span>Attiva rivalsa INPS</span>
              </label>
            </div>
            {tax.inps_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Aliquota INPS %"><input className="input" type="number" step="0.1" min="0" max="100" value={tax.inps_rate || 4} onChange={e => set('tax.inps_rate', Number(e.target.value))} /></Field>
                <Field label="Fondo"><input className="input" value={tax.inps_fund || ''} onChange={e => set('tax.inps_fund', e.target.value)} /></Field>
              </div>
            )}
            <hr style={{borderColor:'var(--color-line)'}} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Importo bollo €"><input className="input" type="number" step="0.01" min="0" value={tax.stamp_duty_amount || 2} onChange={e => set('tax.stamp_duty_amount', Number(e.target.value))} /></Field>
              <Field label="Soglia bollo €"><input className="input" type="number" step="0.01" min="0" value={tax.stamp_duty_threshold || 77.47} onChange={e => set('tax.stamp_duty_threshold', Number(e.target.value))} /></Field>
            </div>
            <Field label="Modalità bollo">
              <select className="input" value={tax.stamp_duty_mode || 'auto'} onChange={e => set('tax.stamp_duty_mode', e.target.value)}>
                <option value="auto">Automatica (se &gt; soglia)</option>
                <option value="always">Sempre</option>
                <option value="never">Mai</option>
              </select>
            </Field>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tax.stamp_duty_virtual} onChange={e => set('tax.stamp_duty_virtual', e.target.checked)} />
                <span>Virtualizzazione bollo (F24 annuale)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Branding con WatermarkPosition */}
        <div className="card-elevated p-6">
          <h2 className="text-lg font-semibold text-surface mb-4">Branding</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-surface-4 mb-2">Logo (PNG/SVG fino a 512 KB)</label>
              <input type="file" accept="image/*" className="input" onChange={handleLogoSelect} />
              {branding.logo_data_url && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={branding.logo_data_url} alt="Logo" className="max-h-20 max-w-40" />
                  <button
                    type="button"
                    className="btn btn-ghost text-xs"
                    onClick={() => set('branding.logo_data_url', '')}
                  >
                    Rimuovi
                  </button>
                </div>
              )}
            </div>

            <WatermarkPosition
              type={branding.watermark_type || 'logo'}
              position={branding.watermark_position || 'center'}
              rotation={branding.watermark_rotation ?? -45}
              size={Number(branding.watermark_size ?? 50)}
              text={branding.watermark_text || ''}
              onChange={handleWatermarkChange}
            />

            <Field label="Colore accentuazione">
              <input className="input" type="color" value={branding.accent_color || '#8b5cf6'} onChange={e => set('branding.accent_color', e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Preventivo */}
        <div className="card-elevated p-6">
          <h2 className="text-lg font-semibold text-surface mb-4">Preventivi</h2>
          <div className="space-y-4">
            <Field label="Template numerazione">
              <input className="input" value={quote.numbering_template || 'PREV-{year}-{seq:4}'} onChange={e => set('quote.numbering_template', e.target.value)} />
              <p className="text-xs text-surface-4 mt-1">Placeholders: {'{year}'}, {'{seq:4}'}, {'{yyyy}'}</p>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Giorni validità"><input className="input" type="number" min="1" value={quote.default_validity_days || 30} onChange={e => set('quote.default_validity_days', Number(e.target.value))} /></Field>
              <Field label="Condizioni pagamento"><input className="input" value={quote.payment_terms_default || ''} onChange={e => set('quote.payment_terms_default', e.target.value)} /></Field>
            </div>
          </div>
        </div>

        {/* UI */}
        <div className="card-elevated p-6">
          <h2 className="text-lg font-semibold text-surface mb-4">Interfaccia</h2>
          <div className="space-y-4">
            <Field label="Tema">
              <select className="input" value={ui.theme || 'dark'} onChange={e => set('ui.theme', e.target.value)}>
                <option value="dark">Scuro</option>
                <option value="light">Chiaro</option>
              </select>
            </Field>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button className="btn btn-secondary" onClick={loadSettings}>Annulla</button>
        <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Salvataggio...' : 'Salva impostazioni'}
        </button>
      </div>
    </>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${className}`}>
      <span className="text-surface-4 text-xs">{label}{required && ' *'}</span>
      {children}
    </label>
  );
}
